import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ShopNav from "@/components/feature/ShopNav";
import ShopFooter from "@/components/feature/ShopFooter";
import {
  supabase,
  getPrimaryImage,
  getEffectivePrice,
  formatPrice,
  type Product,
  type ProductCategory,
} from "@/lib/supabase";

const TEXT_COLOR = "#545454";
const BORDER_SOFT = "rgba(84,84,84,0.14)";
const BORDER_MID = "rgba(84,84,84,0.22)";
const CARD_BG = "#F6F4EF";

export default function CatalogPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | "all">("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [pRes, cRes] = await Promise.all([
          supabase
            .from("product_items")
            .select("*, product_categories(id, name, sort_order)")
            .eq("status", "active")
            .order("id", { ascending: true }),
          supabase.from("product_categories").select("*").order("sort_order"),
        ]);
        if (cancelled) return;
        if (pRes.error) throw pRes.error;
        if (cRes.error) throw cRes.error;
        setProducts((pRes.data ?? []) as Product[]);
        setCategories((cRes.data ?? []) as ProductCategory[]);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Failed to load products";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered =
    activeCategory === "all" ? products : products.filter((p) => p.category_id === activeCategory);

  return (
    <main
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "'Inter Tight', sans-serif",
        color: TEXT_COLOR,
      }}
    >
      <ShopNav variant="light" />

      {/* Hero-style editorial header */}
      <section style={{ padding: "140px 40px 30px", maxWidth: 1400, margin: "0 auto" }}>
        <div className="flex items-center" style={{ gap: 14, marginBottom: 22 }}>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 22,
              color: "rgba(84,84,84,0.55)",
            }}
          >
            (00)
          </span>
          <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
          <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.55)" }}>
            THE COLLECTION · WINTER 2026
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, filter: "blur(12px)", y: 12 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
            fontWeight: 600,
            fontSize: 100,
            lineHeight: 0.95,
            letterSpacing: "-2.4px",
            color: TEXT_COLOR,
            margin: 0,
          }}
        >
          Handmade{" "}
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontWeight: 400,
              letterSpacing: "-3.8px",
            }}
          >
            with love,
          </span>
          <br />
          made to last.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          style={{
            maxWidth: 520,
            fontSize: 15,
            lineHeight: 1.75,
            color: "rgba(84,84,84,0.7)",
            marginTop: 22,
          }}
        >
          Six little companions, each hand-crafted in our Milano atelier from sustainable
          materials. Delivered in a signed cotton pouch, ready to become part of a story.
        </motion.p>
      </section>

      {/* Filter bar */}
      <section
        style={{
          padding: "20px 40px 16px",
          maxWidth: 1400,
          margin: "0 auto",
          borderTop: `1px solid ${BORDER_SOFT}`,
          borderBottom: `1px solid ${BORDER_SOFT}`,
          marginTop: 60,
        }}
      >
        <div
          className="flex items-center justify-between flex-wrap"
          style={{ gap: 20, padding: "6px 0" }}
        >
          <div
            className="flex items-center"
            style={{
              gap: 24,
              fontSize: 10,
              letterSpacing: "2px",
              color: "rgba(84,84,84,0.55)",
              textTransform: "uppercase",
            }}
          >
            <span>Filter</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 18,
                color: "rgba(84,84,84,0.55)",
                letterSpacing: "-0.3px",
              }}
            >
              {filtered.length} pieces
            </span>
          </div>
          <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
            <FilterChip
              active={activeCategory === "all"}
              onClick={() => setActiveCategory("all")}
            >
              All ({products.length})
            </FilterChip>
            {categories.map((c) => {
              const count = products.filter((p) => p.category_id === c.id).length;
              if (count === 0) return null;
              return (
                <FilterChip
                  key={c.id}
                  active={activeCategory === c.id}
                  onClick={() => setActiveCategory(c.id)}
                >
                  {c.name} ({count})
                </FilterChip>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product grid */}
      <section style={{ padding: "50px 40px 100px", maxWidth: 1400, margin: "0 auto" }}>
        {loading && (
          <div
            style={{
              padding: "80px 0",
              textAlign: "center",
              color: "rgba(84,84,84,0.55)",
              fontSize: 12,
              letterSpacing: "2px",
            }}
          >
            LOADING COLLECTION…
          </div>
        )}
        {error && !loading && (
          <div style={{ padding: "80px 0", textAlign: "center" }}>
            <p style={{ color: "#B14837", fontSize: 14 }}>
              Failed to load products: {error}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="cursor-pointer whitespace-nowrap"
              style={{
                marginTop: 16,
                background: TEXT_COLOR,
                color: "#FFFFFF",
                border: 0,
                borderRadius: 999,
                padding: "12px 26px",
                fontSize: 12,
                letterSpacing: "2px",
              }}
            >
              RETRY
            </button>
          </div>
        )}
        {!loading && !error && (
          <div
            className="grid"
            data-product-shop
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 48,
            }}
          >
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div
            style={{
              padding: "80px 0",
              textAlign: "center",
              color: "rgba(84,84,84,0.55)",
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 24,
            }}
          >
            — nothing here yet —
          </div>
        )}
      </section>

      {/* Editorial signature */}
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          padding: "0 40px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          borderTop: `1px solid ${BORDER_SOFT}`,
          paddingTop: 28,
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.5)" }}>
          MAISON DES JOUETS · ATELIER
        </span>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(84,84,84,0.5)",
          }}
        >
          — made slowly, with love
        </span>
      </div>

      <ShopFooter />
    </main>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer whitespace-nowrap transition-all duration-200"
      style={{
        background: active ? TEXT_COLOR : "transparent",
        color: active ? "#FFFFFF" : "rgba(84,84,84,0.75)",
        border: `1px solid ${active ? TEXT_COLOR : BORDER_MID}`,
        borderRadius: 999,
        padding: "8px 16px",
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "1.2px",
      }}
    >
      {children}
    </button>
  );
}

function ProductCard({ product, index }: { product: Product; index: number }) {
  const img = getPrimaryImage(product);
  const price = getEffectivePrice(product);
  const num = String(index + 1).padStart(2, "0");
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: "easeOut" }}
    >
      <Link
        to={`/product/${product.id}`}
        className="cursor-pointer block group"
        style={{ textDecoration: "none", position: "relative" }}
      >
        <div
          style={{
            position: "absolute",
            top: -14,
            left: 2,
            zIndex: 3,
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 18,
            color: "rgba(84,84,84,0.5)",
            pointerEvents: "none",
          }}
        >
          ({num})
        </div>
        <div
          style={{
            position: "relative",
            width: "100%",
            aspectRatio: "3 / 4",
            overflow: "hidden",
            background: CARD_BG,
            borderRadius: 4,
          }}
        >
          <img
            src={img}
            alt={product.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top",
              transition: "transform 0.6s ease",
            }}
            className="group-hover:scale-105"
          />
          {product.stock !== null && product.stock <= 5 && product.stock > 0 && (
            <div
              style={{
                position: "absolute",
                top: 14,
                left: 14,
                background: "#FFFFFF",
                color: TEXT_COLOR,
                padding: "5px 11px",
                fontSize: 10,
                letterSpacing: "1.5px",
                borderRadius: 999,
                fontWeight: 500,
                border: `1px solid ${BORDER_SOFT}`,
              }}
            >
              ONLY {product.stock} LEFT
            </div>
          )}
        </div>
        <div style={{ padding: "18px 2px 0" }}>
          <div className="flex items-start justify-between" style={{ gap: 12 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 26,
                  color: TEXT_COLOR,
                  lineHeight: 1.1,
                  letterSpacing: "-0.6px",
                  fontWeight: 400,
                }}
              >
                {product.name.split("—")[0]?.trim() || product.name}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(84,84,84,0.55)",
                  marginTop: 6,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                }}
              >
                {product.product_categories?.name ?? ""}
              </div>
            </div>
            <div
              style={{
                fontFamily: "'Inter Tight', sans-serif",
                fontSize: 14,
                fontWeight: 500,
                color: TEXT_COLOR,
                whiteSpace: "nowrap",
                paddingTop: 4,
              }}
            >
              {formatPrice(price, product.currency)}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}